import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { strings } from './../../localization';
import EventSummaryItem from './../../components/common/EventReview/EventSummaryItem';
import { csvFormat } from 'd3-dsv';
import moment from 'moment';
import { requestProfilesForIdentities } from './../../data/identities/actions';
import ProfileImage from './../../components/ProfileImage';
import ErrorMessage from './../../components/ErrorMessage';
import SelectDropDown from '../../components/common/SelectDropDown';
import MaintenanceMessage from './MaintenanceMessage';

import { makeEventId } from '../../data/events/actions';
import { makeEventIdWithUnixTimestamp } from '../../data/events/actions';
import RavenUtil from '../../components/common/ravenUtil';
import CognitoUtil from '../../components/aws/cognito/cognitoUtil';

import {
    UNKNOWN_DRIVER_NAME_PLACEHOLDER,
    COACHING_REVIEW_EVENTS_SORT_OPTIONS_NAMES
} from './Constants';

import { EventReviewRcAPI, EVENT_FLAG_IDS, ACTIONS, DISPATCH_HELPERS } from '../../data/review/duck';
import LambdaUtil from '../../components/aws/lambda/lambdaUtil';
import EventSummaryItemCluster from '../../components/common/EventReview/EventSummaryItemCluster';
import ProgressOverlay from '../../components/common/ProgressOverlay';
import { RC_API_EVENT_MEDIA_CAMERA_TYPE, RC_API_EVENT_MEDIA_STATUS } from '../../components/pages/dashboard/EventMedia/eventMediaConstants';


const MAX_PAGE_SIZE = 10;

const COGNITO_UTIL_REFRESH_LOGIN_INTERVAL = 14.5 * 60 * 1000; // 30 seconds shy of 15 mins default timeout
const RCAPI_REFRESH_PROFILES_FOR_IDENTITIES_INTERVAL = 14.5 * 60 * 1000; // 30 seconds shy of 15 mins default timeout

const EXPORT_CSV_COLUMN_HEADERS = {
    "EVENT_TYPE": strings.event_summary_type_label,
    "EVENT_DATE": strings.event_summary_date_label,
    //"EVENT_TIME": strings.event_summary_time_label,
    "VEHICLE_NAME": strings.event_summary_vehicle_name_label,
    "DRIVER_NAME": strings.event_summary_driver_name_label,
}
const EXPORT_CSV_COLUMN_HEADER_KEYS = {
    "EVENT_TYPE": "DimEventType.name",
    "EVENT_DATE": "FactEvent.timestamp",
    //"EVENT_TIME": "FactEvent.timestamp",
    "VEHICLE_NAME": "DimVehicle.name",
    "DRIVER_NAME": "DimDriver.name",
}

const CUBEJS_QUERY_LIMIT = 9000; // Limit for Cube.js queries
const MAX_CONCURRENT_CUBEJS_REQUESTS = 4;
const MAX_CUBEJS_REQUESTS_RETRIES = 3; // Maximum number of retries for errors
const RETRY_CUBEJS_DELAY_MS = 1000; // Delay between retries in milliseconds

const MAX_CONCURRENT_API_REQUESTS_FOR_EVENTS = 4;
const MAX_EVENT_FETCH_RETRIES = 3;
const INITIAL_EVENT_FETCH_RETRY_DELAY_MS = 1000;

function generateCardKey(ravenUuid, cardId) {
    return "card_" + ravenUuid + "_" + cardId;
}

function getDaysBetweenDates (start, end) {
    let now = start.clone(), dates = [];

    while (now.isSameOrBefore(end)) {
        dates.push(now.format('YYYY-MM-DD'));
        now.add(1, 'days');
    }

    return dates;
}

class ReportsSection extends React.PureComponent {

    constructor(props) {
        super(props);

        this.state = {
            loading: false, // Indicates if queries are being executed
            error: null, // Stores any error that occurs during query execution
            totalQueries: 0, // Total number of queries to be executed
            completedQueries: 0, // Number of completed queries
        };

        this.resultSet = null; // Combined results from all queries
        this.mounted = false;
        this.abortController = new AbortController(); // Create an AbortController for query cancellation
    }

    componentDidMount() {
        this.mounted = true;
        this.executeQueries()
            .then(() => {
                this.setState({ loading: false });
            });
    }

    componentWillUnmount() {
        this.mounted = false;
        if (this.abortController) {
            this.abortController.abort(); // Abort all ongoing requests
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.loading) return;

        if (
            prevProps.selectedDateRange !== this.props.selectedDateRange ||
            prevProps.eventTypesFilter !== this.props.eventTypesFilter ||
            prevProps.driverIdsFilter !== this.props.driverIdsFilter ||
            prevProps.vehicleNamesFilter !== this.props.vehicleNamesFilter
        ) {
            this.executeQueries()
                .then(() => {
                    if (this.mounted) {
                        this.setState({ loading: false });
                    }
                })
                .catch((error) => {
                    if (this.mounted) {
                        this.setState({ loading: false, error });
                    }
                });
        }
    }

    executeQueries = async () => {
        // Create new AbortController for each execution
        if (this.abortController) {
            this.abortController.abort();
        }
        this.abortController = new AbortController();
        
        const { cubejsApi, selectedDateRange } = this.props;

        const startDate = moment(selectedDateRange[0]);
        const endDate = moment(selectedDateRange[1]);

        const days = getDaysBetweenDates(startDate, endDate);
        
        const queries = days.map((day, index) => {
            
            let minday = `${day}T00:00:00`;
            let maxday = index < days.length - 1 ? `${days[index + 1]}T00:00:00` : `${day}T23:59:59`;

            // Handle the first day - it should start from the actual selected start time
            if (index === 0) {
                minday = startDate.format('YYYY-MM-DDTHH:mm:ss');
            }

            // Handle the last day - it should end at the actual selected end time
            if (index === days.length - 1) {
                maxday = endDate.format('YYYY-MM-DDTHH:mm:ss');
            }


                return {
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    measures: ["FactEvent.duration"],
                    timeDimensions: [
                        {
                            dimension: "FactEvent.timestamp",
                            dateRange: [minday, maxday],
                        },
                    ],
                    filters: [
                        this.eventTypeFilter(),
                        this.vehicleNameFilter(),
                        this.cardIdFilter(),
                        ...(this.props.features?.facial_recognition &&
                        this.props.facialRecognitionEnabled &&
                        this.props.driverIdsFilter.length > 0
                            ? [this.driverIdentityIdFilter()]
                            : []),
                    ],
                    dimensions: [
                        "DimVehicle.name",
                        "DimDriver.name",
                        "DimDriver.identityId",
                        "FactEvent.timestamp",
                        "FactEvent.position",
                        "DimEventType.name",
                        "DimRaven.uuid",
                        "DimCard.cardId",
                    ],
                    limit: CUBEJS_QUERY_LIMIT,
                };
        });

        this.resultSet = null; // Reset resultSet before executing new queries
        this.setState({ loading: true, error: null, totalQueries: queries.length, completedQueries: 0 });

        const requestQueue = queries.map((query) => ({
            query,
            retryCount: 0,
            status: "queued",
        }));

        return new Promise((resolve, reject) => {
            let hasErrors = false;
            let activeRequests = 0;
            let currentQueueIndex = 0;
            const pendingTimeouts = new Set(); // Keep track of pending timeouts

            // Add abort listener to clean up timeouts
            this.abortController.signal.addEventListener('abort', () => {
                console.warn("Query execution aborted");
                pendingTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
                pendingTimeouts.clear();
            });

            const checkIfComplete = () => {
                if (activeRequests === 0 && currentQueueIndex >= requestQueue.length) {
                    if (hasErrors && this.resultSet.length === 0) {
                        reject(new Error('An error occurred while loading the report'));
                    } else {
                        resolve();
                    }
                }
            };
            
            const processQueue = () => {                

                const attemptFetch = (queryJob) => {
                    const { query, retryCount } = queryJob;

                    cubejsApi
                        .load(query, { signal: this.abortController.signal }) // Pass the abort signal
                        .then((result) => {
                            if (result.error && result.error === "Continue wait") {
                                if (retryCount < MAX_CUBEJS_REQUESTS_RETRIES) {
                                    const delay = RETRY_CUBEJS_DELAY_MS * Math.pow(2, retryCount);
                                    console.warn(
                                        `Retrying query due to 'Continue wait' (attempt ${
                                            retryCount + 1
                                        }/${MAX_CUBEJS_REQUESTS_RETRIES}) after ${delay}ms.`
                                    );
                                    const timeoutId = setTimeout(() => {
                                        pendingTimeouts.delete(timeoutId);
                                        queryJob.retryCount++;
                                        attemptFetch(queryJob);
                                    }, delay);
                                    pendingTimeouts.add(timeoutId);
                                } else {
                                    console.error("Max retries reached for 'Continue wait'.");
                                    queryJob.status = "failed";
                                    hasErrors = true;
                                    activeRequests--;
                                    processQueue();
                                    checkIfComplete();
                                }
                            } else {
                                if(!this.resultSet) this.resultSet = [];
                                this.resultSet.push(...result.rawData());
                                queryJob.status = "success";
                                activeRequests--;
                                this.setState((prevState) => ({
                                    completedQueries: prevState.completedQueries + 1,
                                }));
                                processQueue();
                                checkIfComplete();
                            }
                        })
                        .catch((error) => {
                            if (error.name === "AbortError") {
                                console.warn("Query aborted.");
                                queryJob.status = "aborted";
                                activeRequests--;
                                return;
                            }

                            if (retryCount < MAX_CUBEJS_REQUESTS_RETRIES) {
                                const delay = RETRY_CUBEJS_DELAY_MS * Math.pow(2, retryCount);
                                console.warn(
                                    `Retrying query due to error (attempt ${
                                        retryCount + 1
                                    }/${MAX_CUBEJS_REQUESTS_RETRIES}) after ${delay}ms.`
                                );
                                const timeoutId = setTimeout(() => {
                                    pendingTimeouts.delete(timeoutId);
                                    queryJob.retryCount++;
                                    attemptFetch(queryJob);
                                }, delay);
                                pendingTimeouts.add(timeoutId);
                            } else {
                                console.error("Max retries reached for query error:", error);
                                queryJob.status = "failed";
                                hasErrors = true;
                                activeRequests--;
                                processQueue();
                                checkIfComplete();
                            }
                        });
                };

                while (
                    activeRequests < MAX_CONCURRENT_CUBEJS_REQUESTS &&
                    currentQueueIndex < requestQueue.length
                ) {
                    const queryJob = requestQueue[currentQueueIndex];
                    currentQueueIndex++;

                    if (queryJob.status !== "queued") continue;

                    activeRequests++;
                    queryJob.status = "processing";
                    attemptFetch(queryJob);
                }

                checkIfComplete();
            };

            processQueue(); // Start processing the queue
        });

    };

    eventTypeFilter = () => {
        return {
            "dimension": "DimEventType.name",
            "operator": "equals",
            "values": this.props.eventTypesFilter
        };
    }

    driverIdentityIdFilter = () => {
        return {
            "dimension": "DimDriver.identityId",
            "operator": "equals",
            "values": this.props.driverIdsFilter
        };
    }

    vehicleNameFilter = () => {
        return {
            "dimension": "DimVehicle.name",
            "operator": "equals",
            "values": this.props.vehicleNamesFilter
        };
    }

    cardIdFilter = () => {
        return {
            "dimension": "DimCard.cardId",
            "operator": "set",
            "values": ["true"]
        };
    }

    render() {
        const { loading, error, totalQueries, completedQueries } = this.state;

        if (loading) {
            const percentage = Math.round((completedQueries / totalQueries) * 100);
            return (
                <div>
                    {strings.formatString(strings.loading_data_with_percentage, percentage)}
                </div>
            );
        }

        if (error) {
            return this.renderError(error);
        }

        if (this.resultSet) {
            return (
                <ConnectedEventTable
                    resultSet={this.resultSet}
                    stage={this.props.user.stage}
                    {...this.props}
                />
            );
        }

        return null;
    }

    renderError = error => {
        if (error.message && error.message.match(/maintenance/i)) {
            return <MaintenanceMessage message={error.message} />;
        } else {
            return <ErrorMessage error={error} />;
        }
    }
}

class PaginationControls extends React.PureComponent {

    static propTypes = {
        pageNumber: PropTypes.number.isRequired,
        maxPage: PropTypes.number.isRequired,
        goToPreviousPage: PropTypes.func.isRequired,
        goToNextPage: PropTypes.func.isRequired,
    };

    render() {
        const { pageNumber, maxPage, goToPreviousPage, goToNextPage } = this.props;

        return (
            <div className="control-pagination">
                <button onClick={goToPreviousPage} disabled={pageNumber === 0}><i className={`material-icons chevron_left`}>chevron_left</i></button>
                <div className="coaching-review-results-current-page">{strings.formatString(strings.event_review_page, pageNumber + 1, maxPage + 1)}</div>
                <button onClick={goToNextPage} disabled={pageNumber === maxPage}><i className={`material-icons chevron_right`}>chevron_right</i></button>
            </div>
        );
    }
}

class EventTable extends React.PureComponent {
    constructor(props) {

        super(props);

        this.state = {
            pageNumber: 0, // default first "page"
            pageSize: MAX_PAGE_SIZE,
            cards: null,
            ravenStatuses: {}, // Offline or Online status for each raven

            currentPage: [],
            filteredRows: undefined,

            // Progress tracking
            totalEventsToFetch: 0,
            isLoadingEvents: false,
        }

        this.processedResults = this.props.resultSet ? this.props.resultSet : []; // making resultSet mutable (to merge or expand on the events)

        this.cognitoUtilRefreshLoginInterval = undefined;
        this.rcapiRefreshProfilesForIdentitiesInterval = undefined;

        this.abortController = new AbortController();

    }

    componentDidMount() {
        this.mounted = true;
        this.cognitoUtilRefreshLoginInterval = setInterval(() => { CognitoUtil.refreshLogin(() => { }, () => { }); }, COGNITO_UTIL_REFRESH_LOGIN_INTERVAL);

        this.fetchIdentities();
        this.rcapiRefreshProfilesForIdentitiesInterval = setInterval(() => { this.fetchIdentities(); }, RCAPI_REFRESH_PROFILES_FOR_IDENTITIES_INTERVAL);

        this.fetchEvents();
    }

    componentWillUnmount() {
        this.mounted = false;
        clearInterval(this.cognitoUtilRefreshLoginInterval);
        clearInterval(this.rcapiRefreshProfilesForIdentitiesInterval);

        this.props.clearEventFlags();
        this.props.clearEventNote();

        if(this.abortController) {
            this.abortController.abort();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // Check if getCurrentPage dependencies have changed
        const shouldUpdateCurrentPage = (
            (Object.keys(prevProps.eventFlagsById).length === 0 && Object.keys(this.props.eventFlagsById).length > 0) ||
            (!prevState.eventsFlagsAndNotesFetched && this.state.eventsFlagsAndNotesFetched) ||
            prevProps.sortOptions !== this.props.sortOptions ||
            prevState.pageNumber !== this.state.pageNumber
        );

        if (shouldUpdateCurrentPage) {
            this.getCurrentPage();
        }

        // Change of Status filters can change the number of events displayed, and the pagination so we need to reset the current page.
        if (prevProps.eventFlagsFilterWithState !== this.props.eventFlagsFilterWithState) {
            this.setState({
                pageNumber: 0, // reset to first page
            }, () => {
                this.getCurrentPage();
            });
        }
    }

    get currentPageStartOffset() {
        return this.state.pageNumber * this.state.pageSize;
    }

    get nextPageStartOffset() {
        return (this.state.pageNumber + 1) * this.state.pageSize;
    }

    get maxNextOffset() {
        return this.nextPageStartOffset < this.state.filteredRows.length ? this.nextPageStartOffset : this.state.filteredRows.length;
    }

    getCurrentPage() {

        this.setState({
            isLoadingNewPage: true,
        });

        const { eventFlagsById, eventFlagsFilterWithState } = this.props;
        const {eventsFlagsAndNotesFetched} = this.state;
        const filteredEventFlagsById = {};

        let filteredRows = this.processedResults;

        // If Status filters are selected 
        if(eventFlagsFilterWithState.length > 0) {

            // flags and notes are not fetched yet, do not filter the results
            if(!eventsFlagsAndNotesFetched) return;

            Object.keys(eventFlagsById).forEach(eventKey => {
                const flags = eventFlagsById[eventKey].eventFlags;
                let matchFilters = true;
                eventFlagsFilterWithState.forEach(flagToFilter => {
                    const [flagKey, flagStateValue] = Object.entries(flagToFilter)[0];
                    const filterFlag = EVENT_FLAG_IDS[flagKey];
                    if (!flags[filterFlag] && flagStateValue === 1) {
                        matchFilters = false;
                    }
                    if (flags[filterFlag] && flagStateValue === 2) {
                        matchFilters = false;
                    }
                });

                if (matchFilters) {
                    filteredEventFlagsById[eventKey] = eventFlagsById[eventKey];
                }
            });            

            // Filter the rows based on this.props.eventFlagsFilterWithState. If there are no filters, return all rows
            filteredRows = filteredRows.filter(row => {
                const eventId = makeEventIdWithUnixTimestamp({ ts: row["FactEvent.timestamp"], type: row["DimEventType.name"] });
                const eventKey = [row["DimRaven.uuid"], eventId].join('/');
                return filteredEventFlagsById[eventKey] ? true : false;
            });
        }

        const currentPage = filteredRows.sort(this.compareBySelectedOption).slice(this.currentPageStartOffset, this.nextPageStartOffset);

        this.fetchCards(currentPage)
            .then(([newCards, existingCards]) => {
                if (!this.mounted) return; 
                const cards = [...newCards, ...existingCards];
                cards.forEach(card => this.extractEventsMediaFromCard(card, card.ravenUuid, currentPage));
                return [newCards, currentPage];
            })
            .then(([newCards, updatedCurrentPage]) => {
                if (!this.mounted) return; 
                this.setState({
                    isLoadingNewPage: false,
                    currentPage: updatedCurrentPage,
                    filteredRows,
                    cards: {
                        ...this.state.cards,
                        ...newCards.reduce((acc, card) => {
                            acc[card.cardKey] = card;
                            return acc;
                        }, {})
                    },
                }, () => {                    
                    this.getRavensStatuses();
                });
            });
        
    }

    fetchEvents = () => {
        const events = new Set();

        const rows = this.processedResults;
        rows.forEach(r => {
            const eventId = makeEventIdWithUnixTimestamp({ ts: r['FactEvent.timestamp'], type: r['DimEventType.name'] });
            events.add({
                ravenUuid: r['DimRaven.uuid'],
                eventId: eventId,
                ts: new Date(r['FactEvent.timestamp']).getTime()
            });
        });

        this.setState({
            totalEventsToFetch: events.size,
            isLoadingEvents: true
        });
        
        this.props.requestEvents(this.props.stage, Array.from(events), this.abortController)
            .finally(() => {
                this.setState({
                    eventsFlagsAndNotesFetched: true,
                    isLoadingEvents: false
                });
            });
    };

    fetchCards = (currentPage) => {
        const uniqueCardKeys = new Set();
        const uniqueCards = [];

        currentPage.forEach(r => {
            const cardKey = generateCardKey(r['DimRaven.uuid'], r['DimCard.cardId']);
            if (!uniqueCardKeys.has(cardKey)) {
                uniqueCardKeys.add(cardKey);
                uniqueCards.push(r);
            }
        });

        const promises = [];
        const existingCards = [];

        uniqueCards.forEach(r => {
            if (!this.mounted) return;
            
            const cardId = r['DimCard.cardId'];
            const ravenUuid = r['DimRaven.uuid'];

            const cardKey = generateCardKey(ravenUuid, cardId);

            if (this.state.cards && this.state.cards[cardKey]) {
                console.log("Card already fetched", cardId);
                existingCards.push(this.state.cards[cardKey]);
                return;
            } 
            else if (r.loading) {
                return;
            }
            else {
                r.loading = true; // Indicate that the card is being fetched
            }

            let fetchCardPromise = LambdaUtil.getCard(
                this.props.stage,
                ravenUuid,
                cardId,
            ).then(payload => {
                if (!payload.card) {
                    throw payload;
                }
                payload.card.cardId = cardId;
                payload.card.ravenUuid = ravenUuid;
                payload.card.cardKey = cardKey;
                payload.card.events = this.mergeCardEvents(payload.card);
                return payload.card;
            });

            promises.push(fetchCardPromise);
        });

        if( promises.length <= 0) {
            return Promise.resolve([[], existingCards]);
        }

        return Promise.all(promises).then((newCards) => {
            // All cards have been fetched and processed
            return [newCards, existingCards];
        });
    }

    mergeCardEvents = (card) => {
        if(!card || !Array.isArray(card.events) || card.events.length <= 0) return [];

        const cardGroupedEvents = card.events.reduce((acc, current) => {
            // Separate the 'media' prop from the others
            const { media, ts, type, ...rest } = current;

            // Create a unique key from the key properties
            const key = JSON.stringify({type, ts});

            // If this group doesn't exist yet, create it
            if (!acc[key]) {
                acc[key] = {
                    type,
                    ts,
                    mediaList: [], // Initialize the new array prop
                    ...rest,
                };
            }

            // media can be undefined or null
            if (!media) return acc;

            // Add the current media object to the group's list
            acc[key].mediaList.push(media);

            return acc;
        }, {});

        return Object.values(cardGroupedEvents);
    }

    fetchIdentities = () => {
        if (!this.mounted) return;

        const identityIds = new Set();
        // const driverNames = new Set();
        // driverNames.add(UNKNOWN_DRIVER_NAME_PLACEHOLDER);
        // const vehicleNames = new Set();
        // const eventTypes = new Set();
        const rows = this.processedResults;
        rows.forEach(r => {
            if (r['DimDriver.identityId']) identityIds.add(r['DimDriver.identityId']);
            // if (r['DimEventType.name']) eventTypes.add(r['DimEventType.name']);
            // if (r['DimDriver.name']) driverNames.add(r['DimDriver.name']);
            // if (r['DimVehicle.name']) vehicleNames.add(r['DimVehicle.name']);
        });
        this.props.requestProfilesForIdentities(this.props.stage, Array.from(identityIds));
    }

    getIdentityId = row => {
        if (row['DimDriver.identityId']) {
            return row['DimDriver.identityId'];
        }
        if (row._original)
            if (row._original['DimDriver.identityId']) {
                return row._original['DimDriver.identityId'];
            }
        if (row._subRows && row._subRows[0]) {
            return this.getIdentityId(row._subRows[0]);
        }
        return null;
    }

    extractEventsMediaFromCard = (card, ravenId, currentPage) => {
        
        currentPage.forEach((currentPageItem, index) => {
            if(currentPageItem["processedResults.eventId"]) return;

            // add eventId reference during merge and for passing as a prop during render
            currentPageItem["processedResults.eventId"] = makeEventId({ timestamp: RavenUtil.toDate(currentPageItem["FactEvent.timestamp"]), type: currentPageItem["DimEventType.name"] });
        });

        if (!Array.isArray(card.events) || card.events.length <= 0) {
            console.warn("extractEventsMediaFromCard no card.events", card);
            return;
        }

        if (ravenId.length !== 32) {
            console.warn("ReportsSection EventTable ravenId invalid", ravenId, card);
            return;
        }

        // merge batch query results with reporting query results
        card.events.forEach((eventItem) => {

            const eventId = makeEventId({ timestamp: RavenUtil.toDate(eventItem.ts), type: eventItem.type });
            let processedResultIndex;

            const processedResult = currentPage.find((currentPageItem, index) => {
                if (currentPageItem["processedResults.eventId"] === eventId && 
                    currentPageItem["DimRaven.uuid"] === ravenId &&
                    !currentPageItem["processedResults.mediaSynced"]) {
                    processedResultIndex = index;
                    return true;
                }
                return false;
            });

            if (!processedResult) {
                // console.warn("Coaching EventList processed result not found for card summary object's eventId", eventItem.type);
                return;
            }

            const eventItemMediaIds = Array.isArray(eventItem.mediaList) ? eventItem.mediaList.map(mediaItem => mediaItem.mediaId) : [];
            eventItem.mediaList = Array.isArray(card.media) ? card.media.filter(mediaItem => eventItemMediaIds.includes(mediaItem.mediaId)) : [];
            
            processedResult["processedResults.eventItem"] = eventItem;
            processedResult["processedResults.mediaSynced"] = true;
            currentPage[processedResultIndex] = processedResult; // update processed result
        });
    }

    generateExportCsvURI = () => {

        const outputRows = [];

        this.processedResults.forEach((processedResult) => {

            const eventTypeName = document.getEventName({ type: processedResult[EXPORT_CSV_COLUMN_HEADER_KEYS.EVENT_TYPE] });
            const eventDate = RavenUtil.toDate(processedResult[EXPORT_CSV_COLUMN_HEADER_KEYS.EVENT_DATE]);
            const vehicleName = processedResult[EXPORT_CSV_COLUMN_HEADER_KEYS.VEHICLE_NAME];
            const driverName = processedResult[EXPORT_CSV_COLUMN_HEADER_KEYS.DRIVER_NAME] ? processedResult[EXPORT_CSV_COLUMN_HEADER_KEYS.DRIVER_NAME] : strings.unknown;
            //const eventFlagName = document.getEventFlagName({type: processedResult[EXPORT_CSV_COLUMN_HEADER_KEYS.EVENT_FLAG]});

            const outputRow = {};
            outputRow[EXPORT_CSV_COLUMN_HEADERS.EVENT_TYPE] = eventTypeName;
            outputRow[EXPORT_CSV_COLUMN_HEADERS.EVENT_DATE] = eventDate.toISOString();
            outputRow[EXPORT_CSV_COLUMN_HEADERS.VEHICLE_NAME] = vehicleName;
            outputRow[EXPORT_CSV_COLUMN_HEADERS.DRIVER_NAME] = driverName;
            //outputRow[EXPORT_CSV_COLUMN_HEADERS.EVENT_FLAG] = eventFlagName;
            outputRows.push(outputRow);

        });

        const BOM = '\uFEFF'; // RAV-699 - Fix encoding issue when exported CSV is opened in Excel, add in the BOM for UTF-8 at the beginning of the data in the data URI.
        return `data:text/csv;charset=utf-8,${BOM}${encodeURIComponent(csvFormat(outputRows))}`;
    }

    compareBySelectedOption = (a, b, order) => {

        let selectedSortOption = COACHING_REVIEW_EVENTS_SORT_OPTIONS_NAMES.CHRONOLOGICAL; // default
        const sortOptions = [...this.props.sortOptions];
        for (let i = 0; i < sortOptions.length; i += 1) {
            if (sortOptions[i].checked) {
                selectedSortOption = sortOptions[i].value;
                break;
            }
        }

        let sortItemA;
        let sortItemB;

        const driverNameA = a["DimDriver.name"] ? a["DimDriver.name"] : UNKNOWN_DRIVER_NAME_PLACEHOLDER;
        const driverNameB = b["DimDriver.name"] ? b["DimDriver.name"] : UNKNOWN_DRIVER_NAME_PLACEHOLDER;

        switch (selectedSortOption) {
            case COACHING_REVIEW_EVENTS_SORT_OPTIONS_NAMES.CHRONOLOGICAL:
                sortItemA = RavenUtil.toDate(a["FactEvent.timestamp"]).getTime();
                sortItemB = RavenUtil.toDate(b["FactEvent.timestamp"]).getTime();
                if (sortItemA > sortItemB) { // reverse chrono
                    return -1;
                } else {
                    return 1;
                }
            case COACHING_REVIEW_EVENTS_SORT_OPTIONS_NAMES.VEHICLE:
                sortItemA = a["DimVehicle.name"] + RavenUtil.toDate(a["FactEvent.timestamp"]).getTime();
                sortItemB = b["DimVehicle.name"] + RavenUtil.toDate(b["FactEvent.timestamp"]).getTime();
                break;
            case COACHING_REVIEW_EVENTS_SORT_OPTIONS_NAMES.VEHICLE_DRIVER:
                sortItemA = a["DimVehicle.name"] + driverNameA + RavenUtil.toDate(a["FactEvent.timestamp"]).getTime();
                sortItemB = b["DimVehicle.name"] + driverNameB + RavenUtil.toDate(b["FactEvent.timestamp"]).getTime();
                break;
            case COACHING_REVIEW_EVENTS_SORT_OPTIONS_NAMES.DRIVER:
                sortItemA = driverNameA + RavenUtil.toDate(a["FactEvent.timestamp"]).getTime();
                sortItemB = driverNameB + RavenUtil.toDate(b["FactEvent.timestamp"]).getTime();
                break;
            case COACHING_REVIEW_EVENTS_SORT_OPTIONS_NAMES.DRIVER_VEHICLE:
                sortItemA = driverNameA + a["DimVehicle.name"] + RavenUtil.toDate(a["FactEvent.timestamp"]).getTime();
                sortItemB = driverNameB + b["DimVehicle.name"] + RavenUtil.toDate(b["FactEvent.timestamp"]).getTime();
                break;
            default:
                return 0;
        }
        if (sortItemA < sortItemB) {
            return -1;
        } else {
            return 1;
        }
    }

    goToPreviousPage = () => {

        if (this.state.pageNumber === 0) {
            return;
        }

        this.setState({
            pageNumber: this.state.pageNumber - 1
        });                
    }

    goToNextPage = (maxPage) => {

        if (this.state.pageNumber === maxPage) {
            return;
        }

        this.setState({
            pageNumber: this.state.pageNumber + 1
        });
    }

    applyEventFlag = (...args) => {
        return this.props.applyEventFlag(...args, this.abortController)
        .then(() => {
            this.getCurrentPage();
        })
        .catch(error => {
            return Promise.reject(error);
        });
    }

    applyEventNote = (...args) => {
        return this.props.applyEventNote(...args, this.abortController)
        .then(() => {
            this.getCurrentPage();
        })
        .catch(error => {
            return Promise.reject(error);
        });
    }

    requestEventMedia = (...args) => {
        // it's a dispatch and returns no promise
        // Error handling is done in the redux store itself
        this.props.requestEventMedia(...args, this.abortController);
    }

    getRavensStatuses = () => {
        if (!this.mounted) return;

        if(this.gettingRavensStatuses) return; // already fetching statuses, no need to fetch again
        
        const ravens = new Set(this.state.currentPage.map(row => row["DimRaven.uuid"]));
        const ravensWithoutStatus = [...ravens].filter(ravenUuid => this.state.ravenStatuses[ravenUuid] === undefined);
        
        if (ravensWithoutStatus.length <= 0) return;

        this.gettingRavensStatuses = true;

        ravensWithoutStatus.forEach((ravenUuid, index) => {
            LambdaUtil.getVehicleState(this.props.stage, ravenUuid).then(response => {
                if (!this.mounted) return; // check if still mounted before setting state

                let ravenOffline = false;

                if(!response ||
                    response.status === 'ERROR' ||
                   !response.vehicleState?.hasOwnProperty('ravenOffline')
                ) {
                    console.error("Event Review: Raven status fetch error or no data for ", ravenUuid, response);
                    ravenOffline = true;
                }
                else {
                    ravenOffline = response.vehicleState.ravenOffline;
                }

                this.setState(prevState => ({
                    ravenStatuses: {
                        ...prevState.ravenStatuses,
                        [ravenUuid]: ravenOffline
                    }
                }));
            })
            .catch(error => {
                console.error("Error fetching Raven status for " + ravenUuid, error);
            })
            .finally(() => {
                // all statuses for current page have been fetched, set the flag to false
                if(index === ravensWithoutStatus.length - 1) {
                    this.gettingRavensStatuses = false;
                }
            });
        });
    }

    getEventRoadMediaStatus = (eventItem, eventId, ravenUuid) => {
        const { eventMediaRequestsById } = this.props;

        const roadMediaStatus = eventMediaRequestsById?.[`${ravenUuid}/${eventId}/${RC_API_EVENT_MEDIA_CAMERA_TYPE.CAMERA_ROAD}`]?.status;

        if(roadMediaStatus) {
            return roadMediaStatus;
        }

        if(!eventItem) {
            return undefined;
        }

        const hasRoadMediaSource = eventItem && eventItem.videoAvailable && eventItem.videoAvailable.road;

        return hasRoadMediaSource ? RC_API_EVENT_MEDIA_STATUS.SOURCE_AVAILABLE : RC_API_EVENT_MEDIA_STATUS.SOURCE_GONE;
    }

    getEventCabinMediaStatus = (eventItem, eventId, ravenUuid) => {
        const { eventMediaRequestsById } = this.props;

        const cabinMediaStatus = eventMediaRequestsById?.[`${ravenUuid}/${eventId}/${RC_API_EVENT_MEDIA_CAMERA_TYPE.CAMERA_CABIN}`]?.status;

        if(cabinMediaStatus) {
            return cabinMediaStatus;
        }

        if(!eventItem) {
            return undefined;
        }

        const hasCabinMediaSource = eventItem && eventItem.videoAvailable && eventItem.videoAvailable.cabin;

        return hasCabinMediaSource ? RC_API_EVENT_MEDIA_STATUS.SOURCE_AVAILABLE : RC_API_EVENT_MEDIA_STATUS.SOURCE_GONE;
    }

    render() {
        const {cards, filteredRows} = this.state;
        const { eventFlagsById, eventNoteById, eventMediaRequestsById } = this.props;

        // Not all the data are available yet, so we cannot render the table
        if (!filteredRows) {
            return <div>{strings.event_review_preparing_report}</div>;
        }

        // data is available, but no rows to display
        if (filteredRows.length <= 0) {
            return (
                <div>
                    <h4>{strings.reports_no_data_for_selected_time_period}</h4>
                </div>
            );
        }

        const currentPageStartOffset = this.currentPageStartOffset;
        const maxNextOffset = this.maxNextOffset;
        const maxPage = Math.ceil(filteredRows.length / this.state.pageSize) - 1;

        const exportURI = this.generateExportCsvURI();
        return (
            <div className="event-review-results">
                <div className="event-review-results-controls">
                    <h3>{strings.formatString(strings.reports_number_of_events, filteredRows.length)}</h3>
                    <div className='control-sorting'>
                        <SelectDropDown for={'sort'} nameEmbeddedSelectBox={strings.reports_raven_sort_by} requiredSelection={true} enableFilter={false} options={this.props.sortOptions} singleOnlySelection={true} onChange={this.props.setSortOptions} />
                    </div>
                    <div className="control-pagination">
                        <div className="coaching-review-results-current-showing">{strings.formatString(strings.event_review_events_colon_x_of_y, currentPageStartOffset === 0 ? (currentPageStartOffset + 1) : currentPageStartOffset, maxNextOffset, filteredRows.length)}</div>

                        <PaginationControls
                            pageNumber={this.state.pageNumber}
                            maxPage={maxPage}
                            goToPreviousPage={this.goToPreviousPage}
                            goToNextPage={this.goToNextPage}
                        />
                    </div>
                </div>
                <div className="event-review-results-table">

                    {this.state.isLoadingEvents && 
                        <div className="progress-bar-wrapper">
                            <div className="progress-bar" style={{width: `${(this.props.settingEventsFlagsAndNotesProgress || 5) / this.state.totalEventsToFetch * 100}%` }}>&nbsp;</div>
                        </div>
                    }
                    {this.state.currentPage.map((row, index) => {
                        const eventId = row["processedResults.eventId"] ? row["processedResults.eventId"] : makeEventId({ timestamp: RavenUtil.toDate(row["FactEvent.timestamp"]), type: row["DimEventType.name"] });
                        const eventIdWithUnixTimestamp = makeEventIdWithUnixTimestamp({ ts: row["FactEvent.timestamp"], type: row["DimEventType.name"] });
                        const ts = row["FactEvent.timestamp"];
                        const type = row["DimEventType.name"];
                        const ravenUuid = row["DimRaven.uuid"];
                        const card = cards ? cards[generateCardKey(ravenUuid, row["DimCard.cardId"])] : null;
                        
                        const driverIdentityId = row["DimDriver.identityId"];
                        
                        const newEventFlags = {
                            [EVENT_FLAG_IDS.EVENT_FLAG_REVIEWED]: false,
                            [EVENT_FLAG_IDS.EVENT_FLAG_DISMISSED]: false,
                            [EVENT_FLAG_IDS.EVENT_FLAG_COACHING_REQUIRED]: false,
                            [EVENT_FLAG_IDS.EVENT_FLAG_COACHING_COMPLETED]: false,
                            [EVENT_FLAG_IDS.EVENT_FLAG_UNDER_INVESTIGATION]: false,
                            [EVENT_FLAG_IDS.EVENT_FLAG_FALSE_POSITIVE]: false,
                        };
                        
                        let profileImageComponent = (<ProfileImage
                            identityId={driverIdentityId}
                            stage={this.props.stage}
                            imgSize='72px'
                            expander={false}
                        />);
                        if (this.props.features && this.props.features.facial_recognition && this.props.facialRecognitionEnabled) {
                            if (!driverIdentityId) {
                                profileImageComponent = (<div className="driver-unknown">{profileImageComponent}</div>);
                            }
                        } else {
                            profileImageComponent = null;
                        }
                        return (type === 'EVENT_KLOUD_HISTORICAL_STREAMING' ? 
                        <EventSummaryItemCluster
                            key={eventId + ravenUuid}
                            reportItem={row}
                            eventItem={row["processedResults.eventItem"]}
                            features={this.props.features}
                            profileImageComponent={profileImageComponent}
                            facialRecognitionEnabled={this.props.facialRecognitionEnabled}
                            eventId={eventId}
                            eventIdWithUnixTimestamp={eventIdWithUnixTimestamp}
                            ravenUuid={ravenUuid}
                            ts={ts}
                            type={type}
                            eventFlags={eventFlagsById?.[`${ravenUuid}/${eventIdWithUnixTimestamp}`]?.eventFlags || newEventFlags}
                            eventNote={eventNoteById?.[`${ravenUuid}/${eventIdWithUnixTimestamp}`]?.eventNote || ""}

                            eventFlagPending={eventFlagsById?.[`${ravenUuid}/${eventIdWithUnixTimestamp}`]?.eventFlagPending || false}
                            eventFlagChecked={eventFlagsById?.[`${ravenUuid}/${eventIdWithUnixTimestamp}`]?.eventFlagChecked || false}
                            eventFlagErrorMessage={eventFlagsById?.[`${ravenUuid}/${eventIdWithUnixTimestamp}`]?.eventFlagErrorMessage || ""}
                            applyEventFlag={this.applyEventFlag}
                            timeoutEventFlag={this.props.timeoutEventFlag}

                            eventNotePending={eventNoteById?.[`${ravenUuid}/${eventIdWithUnixTimestamp}`]?.eventNotePending || false}
                            eventNoteChecked={eventNoteById?.[`${ravenUuid}/${eventIdWithUnixTimestamp}`]?.eventNoteChecked || false}
                            eventNoteErrorMessage={eventNoteById?.[`${ravenUuid}/${eventIdWithUnixTimestamp}`]?.eventNoteErrorMessage || ""}
                            applyEventNote={this.applyEventNote}
                            timeoutEventNote={this.props.timeoutEventNote}

                            card={card}
                            stage={this.props.stage}

                            isWaitingForCardEventSummaries={!row["processedResults.mediaSynced"]}
                        />
                        :
                        <EventSummaryItem
                            key={eventId + ravenUuid}
                            reportItem={row}
                            eventItem={row["processedResults.eventItem"]}
                            features={this.props.features}
                            profileImageComponent={profileImageComponent}
                            facialRecognitionEnabled={this.props.facialRecognitionEnabled}
                            eventId={eventId}
                            eventIdWithUnixTimestamp={eventIdWithUnixTimestamp}
                            ravenUuid={ravenUuid}
                            ts={ts}
                            type={type}
                            eventFlags={eventFlagsById?.[`${ravenUuid}/${eventIdWithUnixTimestamp}`]?.eventFlags || newEventFlags}
                            eventNote={eventNoteById?.[`${ravenUuid}/${eventIdWithUnixTimestamp}`]?.eventNote || ""}

                            eventFlagPending={eventFlagsById?.[`${ravenUuid}/${eventIdWithUnixTimestamp}`]?.eventFlagPending || false}
                            eventFlagChecked={eventFlagsById?.[`${ravenUuid}/${eventIdWithUnixTimestamp}`]?.eventFlagChecked || false}
                            eventFlagErrorMessage={eventFlagsById?.[`${ravenUuid}/${eventIdWithUnixTimestamp}`]?.eventFlagErrorMessage || ""}
                            applyEventFlag={this.applyEventFlag}
                            timeoutEventFlag={this.props.timeoutEventFlag}

                            eventNotePending={eventNoteById?.[`${ravenUuid}/${eventIdWithUnixTimestamp}`]?.eventNotePending || false}
                            eventNoteChecked={eventNoteById?.[`${ravenUuid}/${eventIdWithUnixTimestamp}`]?.eventNoteChecked || false}
                            eventNoteErrorMessage={eventNoteById?.[`${ravenUuid}/${eventIdWithUnixTimestamp}`]?.eventNoteErrorMessage || ""}
                            applyEventNote={this.applyEventNote}
                            timeoutEventNote={this.props.timeoutEventNote}

                            requestEventMedia={this.requestEventMedia}
                            previewVideoUrlRoad={eventMediaRequestsById?.[`${ravenUuid}/${eventId}/${RC_API_EVENT_MEDIA_CAMERA_TYPE.CAMERA_ROAD}`]?.eventMedia}
                            previewVideoUrlCabin={eventMediaRequestsById?.[`${ravenUuid}/${eventId}/${RC_API_EVENT_MEDIA_CAMERA_TYPE.CAMERA_CABIN}`]?.eventMedia}
                            eventMediaStatusForRoadCamera={this.getEventRoadMediaStatus(row["processedResults.eventItem"], eventId, ravenUuid)}
                            eventMediaStatusForCabinCamera={this.getEventCabinMediaStatus(row["processedResults.eventItem"], eventId, ravenUuid)}

                            ravenIsOffline={this.state.ravenStatuses[ravenUuid]}

                            card={card}
                            stage={this.props.stage}

                            isWaitingForCardEventSummaries={!row["processedResults.mediaSynced"]}
                        />);
                    })}
                    {this.state.isLoadingNewPage && <ProgressOverlay />}
                </div>
                <div className="event-review-results-footer">
                    <div className="coaching-export">
                        <a
                            download={strings.event_review_export_filename + ".csv"}
                            id="export-link"
                            href={exportURI}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-blue"
                        >
                            {strings.action_export_CSV}
                        </a>
                    </div>
                    <div className="control-pagination">
                        <PaginationControls
                            pageNumber={this.state.pageNumber}
                            maxPage={maxPage}
                            goToPreviousPage={this.goToPreviousPage}
                            goToNextPage={this.goToNextPage}
                        />
                    </div>
                </div>
            </div>
        );
    }

}


EventTable.propTypes = {
    requestProfilesForIdentities: PropTypes.func.isRequired,
    requestEvents: PropTypes.func.isRequired,

    resultSet: PropTypes.array.isRequired,
    stage: PropTypes.string.isRequired,


    eventFlagsById: PropTypes.object,
    eventFlagsFilterWithState: PropTypes.array,
    eventNoteById: PropTypes.object,

    applyEventFlag: PropTypes.func.isRequired,
    timeoutEventFlag: PropTypes.func.isRequired,

    applyEventNote: PropTypes.func.isRequired,
    timeoutEventNote: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => {
    if (!state.data.eventReview) {
        console.error("EventTable mapStateToProps state.data.eventReview is undefined");
        return {};
    }
    return {
        eventFlagsById: state.data.eventReview.eventFlagsById,
        eventNoteById: state.data.eventReview.eventNoteById,
        settingEventsFlagsAndNotesProgress: state.data.eventReview.settingEventsFlagsAndNotesProgress,
        eventMediaRequestsById: state.data.eventReview.eventMediaRequestsById,
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {

    return {
        requestEvents: (stage, eventsToFetch, abortController) => {
            return new Promise((resolve, reject) => {
                const initialEventFlags = Object.keys(EVENT_FLAG_IDS).reduce((acc, key) => {
                    acc[EVENT_FLAG_IDS[key]] = false;
                    return acc;
                }, {});

                eventsToFetch.sort((a, b) => {
                    return b.ts - a.ts; // Sort by timestamp in descending order
                });

                const requestQueue = eventsToFetch.map(event => ({
                    ...event, // ravenUuid, eventId (which is eventIdWithUnixTimestamp)
                    retryCount: 0,
                    status: 'queued'
                }));

                let activeRequests = 0;
                let currentQueueIndex = 0;
                const pendingTimeouts = new Set(); // Keep track of pending timeouts

                const checkIfComplete = () => {
                    if (activeRequests === 0 && currentQueueIndex >= requestQueue.length) {
                        resolve();
                    }
                };

                // Add a listener to the abort signal to clear all pending timeouts
                abortController.signal.addEventListener('abort', () => {
                    console.warn("Event fetch aborted");
                    pendingTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
                    pendingTimeouts.clear();

                    dispatch(ACTIONS.clearEventFlags());
                    dispatch(ACTIONS.clearEventNote());

                });

                const processQueue = () => {

                    // Check abort signal before processing
                    if (abortController.signal.aborted) {
                        return;
                    }
                
                    while (activeRequests < MAX_CONCURRENT_API_REQUESTS_FOR_EVENTS && currentQueueIndex < requestQueue.length) {
                        const eventJob = requestQueue[currentQueueIndex];
                        currentQueueIndex++;

                        if (eventJob.status !== 'queued') continue;

                        // Check abort signal before starting new request
                        if (abortController.signal.aborted) {
                            return;
                        }

                        activeRequests++;
                        eventJob.status = 'processing';
                        const { ravenUuid, eventId } = eventJob; // eventId is eventIdWithUnixTimestamp

                        dispatch(ACTIONS.updatingEventFlag(undefined, ravenUuid, eventId, { ...initialEventFlags }));
                        dispatch(ACTIONS.updatingEventNote(undefined, ravenUuid, eventId, ""));

                        const attemptFetch = (retryAttempt) => {
                            // Check abort signal before each retry attempt
                            if (abortController.signal.aborted) {
                                return;
                            }

                            EventReviewRcAPI.getSingleEvent(stage, ravenUuid, eventId, abortController) // Modified: no dispatch inside
                                .then((response) => {
                                    
                                    // Check abort signal before dispatching success
                                    if (abortController.signal.aborted) {
                                        return;
                                    }

                                    eventJob.status = 'success';
                                    activeRequests--;

                                    const eventNote = response.note || '';
                                    const fetchedEventFlags = { ...initialEventFlags };
                                    if (Array.isArray(response.flaglist)) {
                                        response.flaglist.forEach(f => {
                                            if (fetchedEventFlags.hasOwnProperty(f)) {
                                                fetchedEventFlags[f] = true;
                                            }
                                        });
                                    }
                                    dispatch(ACTIONS.setEventNote(undefined, undefined, ravenUuid, eventId, eventNote));
                                    dispatch(ACTIONS.setEventFlags(undefined, undefined, ravenUuid, eventId, fetchedEventFlags));
                                    processQueue();
                                    checkIfComplete();
                                })
                                .catch((error) => {
                                    // Check for abort error first
                                    if (abortController.signal.aborted || error.name === 'AbortError') {
                                        activeRequests--;
                                        eventJob.status = 'aborted';
                                        return;
                                    }

                                    if (error && error.code === 500 && retryAttempt < MAX_EVENT_FETCH_RETRIES) {
                                        const delay = INITIAL_EVENT_FETCH_RETRY_DELAY_MS * Math.pow(2, retryAttempt);
                                        console.warn(`Retrying event ${eventId} (attempt ${retryAttempt + 1}/${MAX_EVENT_FETCH_RETRIES}) after ${delay}ms due to 500 error.`);
                                        const timeoutId = setTimeout(() => {
                                            pendingTimeouts.delete(timeoutId);
                                            attemptFetch(retryAttempt + 1);
                                        }, delay);
                                        pendingTimeouts.add(timeoutId);
                                    } else {
                                        eventJob.status = 'failed';
                                        activeRequests--;
                                        console.error(`Failed to fetch event ${eventId} after ${retryAttempt +1} retries or due to non-500 error:`, error);
                                        const errorMessage = (error && error.message) ? error.message : strings.event_note_error_request_error;
                                        dispatch(ACTIONS.setEventNote(undefined, errorMessage, ravenUuid, eventId, ""));
                                        dispatch(ACTIONS.setEventFlags(undefined, errorMessage, ravenUuid, eventId, { ...initialEventFlags }));
                                        processQueue();
                                        checkIfComplete();
                                    }
                                });
                        };
                        attemptFetch(0);
                    }
                    checkIfComplete();
                };

                processQueue();
            });
        },

        requestProfilesForIdentities: (stage, identityIds) => {
            return dispatch(requestProfilesForIdentities(stage, identityIds));
        },
        clearEventFlags: () => {
            return dispatch(ACTIONS.clearEventFlags());
        },
        clearEventNote: () => {
            return dispatch(ACTIONS.clearEventNote());
        },

        applyEventFlag: (stage, isChecked, ravenUuid, eventId, flagName, flags, abortController) => {
            dispatch(ACTIONS.updatingEventFlag(isChecked, ravenUuid, eventId, flags));
            const method = isChecked ? EventReviewRcAPI.putEventFlag : EventReviewRcAPI.deleteEventFlag;
            return method(stage, ravenUuid, eventId, flagName, abortController)
                .then(
                    () => {
                        dispatch(ACTIONS.setEventFlags(isChecked, undefined, ravenUuid, eventId, flags));
                        return Promise.resolve();
                    },
                    (error) => { return Promise.reject(error); }
                )
                .catch((error) => {
                    dispatch(ACTIONS.setEventFlags(!isChecked, strings.event_flag_error_request_error, ravenUuid, eventId, flags));
                    return Promise.reject(error);
                });
        },

        timeoutEventFlag: (isChecked, ravenUuid, eventId, flags) => {
            return dispatch(ACTIONS.setEventFlags(isChecked, strings.event_flag_error_request_timeout, ravenUuid, eventId, flags));
        },

        applyEventNote: (stage, isSaved, ravenUuid, eventId, note, abortController) => {
            dispatch(ACTIONS.updatingEventNote(isSaved, ravenUuid, eventId, note));
            const method = (note.length > 0) ? EventReviewRcAPI.updateEventNote(stage, ravenUuid, eventId, note, abortController) : EventReviewRcAPI.deleteEventNote(stage, ravenUuid, eventId, abortController);
            return method
                .then(
                    () => {
                        dispatch(ACTIONS.setEventNote(isSaved, undefined, ravenUuid, eventId, note));
                        return Promise.resolve();
                    },
                    (error) => { return Promise.reject(error); }
                )
                .catch((error) => {
                    dispatch(ACTIONS.setEventNote(!isSaved, strings.event_note_error_request_error, ravenUuid, eventId, note));
                    return Promise.reject(error);
                });
        },

        timeoutEventNote: (isSaved, ravenUuid, eventId, note) => {
            return dispatch(ACTIONS.setEventNote(isSaved, strings.event_note_error_request_timeout, ravenUuid, eventId, note));
        },

        requestEventMedia: (stage, ravenUuid, eventId, cameraType, timestamp, duration, eventType, abortController) => {
             dispatch(DISPATCH_HELPERS.initiateMediaRequest(stage, ravenUuid, eventId, cameraType, timestamp, duration, eventType, abortController));
        }

    };
};

const ConnectedEventTable = connect(mapStateToProps, mapDispatchToProps)(EventTable);


ReportsSection.propTypes = {
    selectedDateRange: PropTypes.instanceOf(Array).isRequired
}
export default ReportsSection;

